/** Accept only an explicitly parsed, finite, non-negative VAT amount. */
export function isValidVatAmount(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

export type VatBreakdownItem = { rate: number; amount: number };

/** Read explicitly labelled VAT-rate totals from the VAT summary block of a Turkish invoice.
 * Amounts use Turkish formatting (1.234,56 TL). Never infer a missing rate or amount.
 */
export function parsePdfVatBreakdown(lines: string[]): VatBreakdownItem[] {
  const parseAmount = (raw: string) =>
    Math.round((Number(raw.replace(/\./g, "").replace(",", ".")) + Number.EPSILON) * 100) / 100;

  // Do not search for the first number after a VAT rate: the remainder of a PDF's
  // flattened text can include product-line VAT amounts (e.g. 227,27 TL).
  // Require the amount to immediately follow the explicitly labelled summary row.
  // Joining lines first supports PDF.js splitting the label/rate/amount across lines.
  const text = lines.join(" ").replace(/\s+/g, " ").toLocaleLowerCase("tr-TR");
  const rowPattern =
    /hesaplanan\s*(?:gerçek\s*usulde\s*)?(?:katma\s*değer\s*vergisi|kdv)\s*\(?\s*%\s*(\d{1,2})(?:[,.]\d+)?\s*\)?\s*[:：]?\s*([\d.]+,\d{2})\s*tl\b/g;
  const result: VatBreakdownItem[] = [];

  for (const match of text.matchAll(rowPattern)) {
    const rate = Number(match[1]);
    const amount = parseAmount(match[2]);
    if (Number.isInteger(rate) && rate >= 0 && Number.isFinite(amount) && amount >= 0) {
      result.push({ rate, amount });
    }
  }

  return result.filter((item, index, all) =>
    all.findIndex(other => other.rate === item.rate && other.amount === item.amount) === index
  );
}
